import {DestroyRef, Injectable} from '@angular/core';
import {SpatialUtilService} from './spatial-util.service';
import {AlertModalService} from '../../common/component/modal';
import {NgEventBus} from 'ng-event-bus';
import {catchError, map, Observable, of, switchMap, throwError} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AngleUpdateService} from './features/angle/angle-update.service';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';
import {PolygonService} from './features/polygon.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class FeatureApplyUpdateService {
    title = 'FeaturesApplyUpdateService';

    constructor(
        private angleUpdateService: AngleUpdateService,
        private esriModuleService: EsriModuleService,
        private spatialUtilService: SpatialUtilService,
        private alertModalService: AlertModalService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef,
        private polygonService: PolygonService
    ) {}

    updateFeaturesByType(featureLayer: any, features: any[], callback?) {
        const observable: Observable<any> = of(null);

        const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);
        console.debug(this.title + ' : updateFeaturesByType for ' + featureLayerType + ' SIZE: ' + (features ? features.length : '0'));

        const graphics = this.spatialUtilService.getGraphics(featureLayer);

        const featuresBeforeUpdate = features
            .map(f => graphics.find(g => g.attributes.id === f.attributes.id))
            .filter(g => g)
            .map(g => new this.esriModuleService.Graphic(g.toJson()));

        if (featuresBeforeUpdate.length === 0) {
            if (callback) {
                callback();
            }
            return of([]);
        }

        return this.spatialUtilService.applyEdits(featureLayer, null, features, null).pipe(
            switchMap(res => {
                console.debug(this.title + ' : updateFeaturesByType updates - TYPE: ' + featureLayer.id + ' - RESULT: ', res.updates);
                if (res.updates && res.updates.every(u => u.success || u.error.code === 1011)) {
                    const featureIds = features.map(f => f.attributes.id);
                    const featuresWithFullDetails = this.spatialUtilService
                        .getGraphics(featureLayer)
                        .filter(f => featureIds.includes(f.attributes.id));

                    let promises: Observable<any>[] = [];

                    if (featuresWithFullDetails.length && featuresWithFullDetails[0].attributes.x && featuresWithFullDetails[0].attributes.y) {
                        promises = featuresWithFullDetails.map(f => {
                            const fbu = featuresBeforeUpdate.filter(e => e.attributes.id === f.attributes.id)[0];
                            if (fbu && (f.attributes.x !== fbu.attributes.x || f.attributes.y !== fbu.attributes.y)) {
                                //TODO [PAU-373]: Integrate featuresMovingService.movePoint when featuresMovingService is ready
                                //return featuresMovingService.movePoint(fbu, f, featureLayer);
                                return observable;
                            }

                            return observable;
                        });
                    } else {
                        promises.push(observable);
                    }

                    const metaData = {
                        featuresWithFullDetails: featuresWithFullDetails,
                        featuresBeforeUpdate: featuresBeforeUpdate
                    };

                    return forkJoinDefaultIfEmpty(promises).pipe(
                        switchMap(() => {
                            if (featureLayerType !== 'angle' && featureLayerType !== 'annotation' && featureLayerType !== 'clipping') {
                                featuresWithFullDetails.forEach(feature => {
                                    this.updateAnnotations(featureLayerType, featuresBeforeUpdate, feature);
                                });
                            }

                            if (['angle', 'annotation'].includes(featureLayerType)) {
                                this.spatialUtilService.getMapFeatureLayer(featureLayerType).redraw();
                            }

                            if (featureLayerType === 'angle') {
                                return this.angleUpdateService
                                    .updateRelatedAngleAttributes(featuresWithFullDetails)
                                    .pipe(map(() => featuresWithFullDetails));
                            } else if (featureLayerType === 'line') {
                                return this.angleUpdateService
                                    .updateAngleTypesIfRequired(featuresWithFullDetails, featuresBeforeUpdate)
                                    .pipe(map(() => featuresWithFullDetails));
                            } else if (featureLayerType === 'lot') {
                                return this.polygonService
                                    .updatePolygonTypesIfRequired(featuresWithFullDetails, featuresBeforeUpdate)
                                    .pipe(map(() => featuresWithFullDetails));
                            } else if (featureLayerType === 'polygon') {
                                this.eventBus.cast('updateLotAreaIfRequired', metaData);
                            }

                            return of(featuresWithFullDetails);
                        }),
                        catchError(() => {
                            return throwError(() => {
                                console.error(`${this.title}: Error updating features`);
                                new Error();
                            });
                        })
                    );
                } else if (res.updates) {
                    return throwError(() => {
                        const unsuccessfulUpdates = res.updates.filter(u => u.error && u.error.code !== 1011);
                        console.error('Error updating features:', unsuccessfulUpdates);
                        new Error();
                    });
                } else {
                    return observable;
                }
            }),
            catchError(error => this.alertModalService.createAlertMessage('Error updating features. Error : ' + error, 'Ok')),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    updateAnnotations(featureLayerType: any, featuresBeforeUpdate: any, feature: any) {
        console.debug(this.title + ' : updateAnnotations');

        const featureBeforeUpdate = featuresBeforeUpdate.find(f => f.attributes.id === feature.attributes.id);

        if (
            (feature.attributes.type && featureBeforeUpdate.attributes.type !== feature.attributes.type) ||
            ((featureLayerType === 'point' || featureLayerType === 'controlPoint') &&
                (feature.attributes.x !== featureBeforeUpdate.attributes.x || feature.attributes.y !== featureBeforeUpdate.attributes.y))
        ) {
            const annotationsToDelete = this.spatialUtilService.toApplyEditsDeletes(
                'annotation',
                this.spatialUtilService
                    .getAnnotations()
                    .filter(a => a.attributes.featuretype === featureLayerType && a.attributes.featureid === feature.attributes.id)
            );

            (annotationsToDelete.length ? this.spatialUtilService.applyEdits('annotation', null, null, annotationsToDelete) : of(null)).subscribe(
                () =>
                    this.eventBus.cast('createAnnotation', {
                        feature: feature,
                        featureType: featureLayerType
                    })
            );
        } else {
            let updateGeometry = false;
            let createAnnotation = true;
            const tol = 0.001;

            if (featureLayerType !== 'lot' && featureLayerType !== 'interest' && featureLayerType !== 'line') {
                updateGeometry = true;
            } else if (
                featureLayerType === 'line' &&
                (!this.spatialUtilService.tolEquals(feature.geometry.paths[0][0][0], featureBeforeUpdate.geometry.paths[0][0][0], tol) ||
                    !this.spatialUtilService.tolEquals(feature.geometry.paths[0][0][1], featureBeforeUpdate.geometry.paths[0][0][1], tol) ||
                    !this.spatialUtilService.tolEquals(feature.geometry.paths[0][1][0], featureBeforeUpdate.geometry.paths[0][1][0], tol) ||
                    !this.spatialUtilService.tolEquals(feature.geometry.paths[0][1][1], featureBeforeUpdate.geometry.paths[0][1][1], tol))
            ) {
                updateGeometry = true;
            } else if (featureLayerType === 'interest' && !feature.attributes.subject) {
                const interest = this.spatialUtilService.copyGraphic(this.spatialUtilService.findById('interest', feature.attributes.id));
                this.spatialUtilService.parseIdsAttr(feature.attributes.polygonids).forEach(polygonId => {
                    this.eventBus.cast('saveInterestDissociation', {
                        interest: interest,
                        childId: polygonId,
                        childType: 'polygon'
                    });
                });
                this.spatialUtilService.parseIdsAttr(feature.attributes.polylineids).forEach(polylineId => {
                    this.eventBus.cast('saveInterestDissociation', {
                        interest: interest,
                        childId: polylineId,
                        childType: 'polyline'
                    });
                });
            }

            if (featureLayerType === 'road' || featureLayerType === 'interest') {
                createAnnotation = false;
            }

            if (featureLayerType === 'controlPoint') {
                featureLayerType = 'point';
            }

            const metaDataForUpdateAnnotation = {
                feature: feature,
                featureType: featureLayerType,
                updateGeometry: updateGeometry,
                createAnnotation: createAnnotation
            };
            this.eventBus.cast('updateAnnotation', metaDataForUpdateAnnotation);
        }
    }
}
